const MEDIAKIT_SHORT_EDGE = Object.freeze({ '720p': 720, '1080p': 1080, '2k': 1440, '4k': 2160 });

const parseSize = (value) => {
  const match = /^(\d{3,5})x(\d{3,5})$/i.exec(String(value || '').trim());
  return match ? { width: Number(match[1]), height: Number(match[2]) } : null;
};

const even = (value) => Math.max(2, Math.round(value / 2) * 2);

export function meetsStandaloneEnhanceTier(actual, target) {
  const values = [actual?.width, actual?.height, target?.width, target?.height];
  if (!values.every((value) => Number.isInteger(value) && value > 0)) return false;
  if ((actual.width >= actual.height) !== (target.width >= target.height)) return false;
  // Resolution tiers describe the short edge. The provider owns the final
  // aspect ratio in standalone enhancement, so do not compare the long edge.
  return Math.min(actual.width, actual.height) >= Math.min(target.width, target.height) - 2;
}

export function resolveSeedanceSrPlan({ sourceSize, targetSize, aspectRatio, outputFormat = 'mp4', allow4kUpscale = false }) {
  const source = parseSize(sourceSize);
  if (!source) throw new Error('A measured source size is required.');
  const adaptiveMatch = /^adaptive_(720p|1080p|2k|4k)$/i.exec(String(targetSize || '').trim());
  const adaptiveShortEdge = adaptiveMatch ? MEDIAKIT_SHORT_EDGE[adaptiveMatch[1].toLowerCase()] : null;
  const target = adaptiveShortEdge
    ? (source && source.width >= source.height
      ? { width: even(adaptiveShortEdge * source.width / source.height), height: adaptiveShortEdge }
      : { width: adaptiveShortEdge, height: even(adaptiveShortEdge * source.height / source.width) })
    : parseSize(targetSize);
  if (!source || !target) throw new Error('A measured source size and a fixed target size are required.');
  const ratio = adaptiveMatch ? 'adaptive' : String(aspectRatio || '').toLowerCase();
  const normalizedSource = { ...source };
  if (!adaptiveMatch) {
    const widthAtTargetRatio = source.height * target.width / target.height;
    const heightAtTargetRatio = source.width * target.height / target.width;
    // Model-native 480p dimensions are sometimes a few pixels off the product
    // ratio. Crop before generative enhancement so it sees the intended frame.
    // Keep rounding differences of at most two pixels (e.g. 854x480 → 1922x1080).
    if (source.width - widthAtTargetRatio > 2) {
      normalizedSource.width = Math.max(2, Math.floor(widthAtTargetRatio / 2) * 2);
    } else if (source.height - heightAtTargetRatio > 2) {
      normalizedSource.height = Math.max(2, Math.floor(heightAtTargetRatio / 2) * 2);
    }
  }

  const shortEdge = Math.min(target.width, target.height);
  const mediakitResolution = Object.entries(MEDIAKIT_SHORT_EDGE)
    .find(([, pixels]) => pixels >= shortEdge)?.[0]
    || (allow4kUpscale && shortEdge <= 2880 ? '4k' : null);
  if (!mediakitResolution) throw new Error('The target short edge exceeds MediaKit 4K support.');
  const mediakitShortEdge = MEDIAKIT_SHORT_EDGE[mediakitResolution];
  const sourceIsLandscape = normalizedSource.width >= normalizedSource.height;
  const expectedSize = sourceIsLandscape
    ? { width: even(mediakitShortEdge * normalizedSource.width / normalizedSource.height), height: mediakitShortEdge }
    : { width: mediakitShortEdge, height: even(mediakitShortEdge * normalizedSource.height / normalizedSource.width) };
  const sizeDelta = Math.max(Math.abs(expectedSize.width - target.width), Math.abs(expectedSize.height - target.height));
  const needsPostTransform = sizeDelta > 2 || outputFormat.toLowerCase() === 'mov';
  const needsPreTransform = normalizedSource.width !== source.width || normalizedSource.height !== source.height;
  return {
    source,
    normalizedSource,
    target,
    aspectRatio: ratio,
    mediakitResolution,
    expectedSize,
    needsPreTransform,
    needsPostTransform,
    postTransformEngine: needsPostTransform
      ? (Math.max(target.width, target.height) > 2000 || outputFormat.toLowerCase() === 'mov' ? 'ffmpeg-container' : 'cloudflare-media')
      : null,
    outputFormat: outputFormat.toLowerCase(),
    adaptive: Boolean(adaptiveMatch),
  };
}

export function resolveStandaloneEnhanceResolution({ sourceWidth, sourceHeight, resolution, engine = 'generative' }) {
  const width = Number(sourceWidth);
  const height = Number(sourceHeight);
  const target = String(resolution || '').toLowerCase();
  const shortEdge = MEDIAKIT_SHORT_EDGE[target];
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 || !shortEdge) {
    throw new Error('Valid source dimensions and target resolution are required.');
  }
  const sourceLimit = engine === 'fast' ? 1440 : 1080;
  if (Math.min(width, height) > sourceLimit) throw new Error('Source resolution exceeds this enhancer input limit.');
  if (Math.max(width, height) > (engine === 'fast' ? 2560 : 1920)) throw new Error('Source resolution exceeds this enhancer input limit.');
  if (shortEdge <= Math.min(width, height)) throw new Error('Select an output resolution above the source resolution.');
  return width >= height
    ? { width: even(shortEdge * width / height), height: shortEdge, resolution: target }
    : { width: shortEdge, height: even(shortEdge * height / width), resolution: target };
}
